import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { getAllPages } from '@/lib/paginated-data';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn, formatDate } from '@/lib/utils';
import type { Document, ApiResponse } from '@/types';
import {
  FileText, Upload, Search, Filter, Trash2, Download,
  Image, FileSpreadsheet, File, FileArchive, Loader2,
  FolderOpen, Eye, MoreVertical, Files, HardDrive,
} from 'lucide-react';
import { ContextMenu } from '@/components/common/ContextMenu';

type CofundingApplicationListItem = {
  id: string;
  program_name: string;
  status: string;
  period_start: string;
  period_end: string;
  partner_company: string;
  created_at: string;
};

const ENTITY_TYPES = [
  { value: '', label: 'Alle Kategorien' },
  { value: 'plan', label: 'Marketingpläne' },
  { value: 'activity', label: 'Aktivitäten' },
  { value: 'campaign', label: 'Kampagnen' },
  { value: 'supplier', label: 'Zulieferer' },
  { value: 'profile', label: 'Profile' },
];

function getFileIcon(mimeType?: string) {
  if (!mimeType) return <File size={20} />;
  if (mimeType.startsWith('image/')) return <Image size={20} className="text-accent" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return <FileSpreadsheet size={20} className="text-success" />;
  if (mimeType.includes('pdf')) return <FileText size={20} className="text-danger" />;
  if (mimeType.includes('zip') || mimeType.includes('archive'))
    return <FileArchive size={20} className="text-warning" />;
  return <FileText size={20} className="text-muted" />;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadEntity, setUploadEntity] = useState({ type: 'plan', id: '' });
  const [dragActive, setDragActive] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'size_bytes'>('created_at');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cofundingApps, setCofundingApps] = useState<CofundingApplicationListItem[]>([]);
  const [cofundingLoading, setCofundingLoading] = useState(false);
  const [selectedCofundingId, setSelectedCofundingId] = useState<string>('');
  const [pdfExporting, setPdfExporting] = useState<'antrag' | 'proof' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadRequest = useRef(0);

  useEffect(() => {
    void loadCofundingApplications();
  }, []);

  const loadDocuments = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (entityType) params.set('entity_type', entityType);
      const documents = await getAllPages<Document>(`/documents?${params}`);
      if (request === loadRequest.current) setDocuments(documents);
    } catch (err) {
      if (request !== loadRequest.current) return;
      setDocuments([]);
      setError(err instanceof Error ? err.message : 'Dokumente konnten nicht geladen werden.');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    void loadDocuments();
    return () => { loadRequest.current += 1; };
  }, [loadDocuments]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || uploading) return;
    setError(null);
    setSuccess(null);
    if (!uploadEntity.id.trim()) {
      setError('Bitte eine Referenz-ID angeben, damit Dokumente korrekt zugeordnet werden.');
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', uploadEntity.type);
        formData.append('entity_id', uploadEntity.id.trim());
        await api.upload<ApiResponse>('/documents/upload', formData);
      }
      await loadDocuments();
      setShowUpload(false);
      setSuccess(`${files.length} Datei(en) erfolgreich hochgeladen.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadCofundingApplications = async () => {
    setCofundingLoading(true);
    try {
      const res = await api.get<ApiResponse<CofundingApplicationListItem[]>>('/export/cofunding/applications');
      const apps = res.data ?? [];
      setCofundingApps(apps);
      const firstApp = apps[0];
      if (firstApp?.id) {
        setSelectedCofundingId((prev) => prev || firstApp.id);
      }
    } catch {
      // Keep document page usable even if co-funding export is unavailable
    } finally {
      setCofundingLoading(false);
    }
  };

  const handleCofundingPdfExport = async (kind: 'antrag' | 'proof') => {
    if (!selectedCofundingId) {
      setError('Bitte zuerst eine Co-Funding-Applikation auswählen.');
      return;
    }
    setError(null);
    setSuccess(null);
    setPdfExporting(kind);
    try {
      const blob = await api.getBlob(`/export/cofunding/application/${selectedCofundingId}/${kind}.pdf`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cofunding-${kind}-${selectedCofundingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSuccess(`${kind === 'antrag' ? 'Antrag' : 'Proof'} PDF wurde erstellt und heruntergeladen.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF-Export fehlgeschlagen.');
    } finally {
      setPdfExporting(null);
    }
  };

  const handleDownload = async (doc: Document) => {
    setError(null);
    const preview = window.open('', '_blank');
    if (preview) preview.opener = null;
    try {
      const res = await api.get<ApiResponse<{ url: string }>>(`/documents/${doc.id}/download`);
      if (!res.data?.url) throw new Error('Download-Link konnte nicht geladen werden.');
      if (!preview) throw new Error('Bitte Pop-ups für den Download erlauben.');
      preview.location.href = res.data.url;
    } catch (error) {
      preview?.close();
      setError(error instanceof Error ? error.message : 'Download fehlgeschlagen.');
    }
  };

  const handleDeactivate = async (id: string) => {
    setError(null);
    try {
      await api.patch(`/documents/${id}/deactivate`);
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Dokument konnte nicht entfernt werden.');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleUpload(e.dataTransfer.files);
  };

  const filtered = documents
    .filter((d) => !search || d.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'size_bytes') return (b.size_bytes ?? 0) - (a.size_bytes ?? 0);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  const totalStorageBytes = useMemo(
    () => documents.reduce((sum, doc) => sum + (doc.size_bytes ?? 0), 0),
    [documents]
  );
  const imageCount = useMemo(
    () => documents.filter((doc) => (doc.mime_type ?? '').startsWith('image/')).length,
    [documents]
  );

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card">
        <div className="p-5 lg:p-6">
            <h1 className="text-2xl font-semibold text-foreground">Dokumente</h1>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card-hover/60">
                <p className="text-xs text-muted">Dateien</p>
                <p className="text-lg font-semibold text-foreground">{documents.length}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card-hover/60">
                <p className="text-xs text-muted">Bilder</p>
                <p className="text-lg font-semibold text-foreground">{imageCount}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3 dark:bg-card-hover/60">
                <p className="text-xs text-muted">Speicher</p>
                <p className="text-lg font-semibold text-foreground">{formatFileSize(totalStorageBytes)}</p>
              </div>
            </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted">
          <Files className="h-3.5 w-3.5" />
          <HardDrive className="h-3.5 w-3.5" />
          Übersicht, Upload und PDF-Export
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover transition-colors"
        >
          <Upload size={16} /> Hochladen
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 dark:bg-card-hover">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 basis-64">
            <p className="text-sm font-semibold text-foreground">Swisscom Co-Funding PDF Export</p>
            <p className="mt-1 text-xs text-muted">
              Direkter Export aus dem im System generierten Content (Antrag + Proof).
            </p>
            <select
              value={selectedCofundingId}
              onChange={(e) => setSelectedCofundingId(e.target.value)}
              disabled={cofundingLoading || cofundingApps.length === 0}
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
            >
              {cofundingApps.length === 0 && <option value="">Keine Co-Funding-Applikation gefunden</option>}
              {cofundingApps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.program_name} ({formatDate(app.period_start)} - {formatDate(app.period_end)}) [{app.status}]
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => handleCofundingPdfExport('antrag')}
            disabled={!selectedCofundingId || pdfExporting !== null}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pdfExporting === 'antrag' ? 'Erstelle Antrag...' : 'Antrag PDF'}
          </button>
          <button
            onClick={() => handleCofundingPdfExport('proof')}
            disabled={!selectedCofundingId || pdfExporting !== null}
            className="rounded-lg bg-success px-4 py-2 text-sm font-medium text-success-foreground hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pdfExporting === 'proof' ? 'Erstelle Proof...' : 'Proof PDF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          {success}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[250px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Dokumente suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm dark:bg-card-hover dark:text-foreground focus:border-border focus:outline-none focus:ring-2 focus:ring-border/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-muted" />
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm dark:bg-card-hover dark:text-foreground focus:border-border focus:outline-none"
          >
            {ENTITY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'created_at' | 'size_bytes')}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm dark:bg-card-hover dark:text-foreground focus:border-border focus:outline-none"
          >
            <option value="created_at">Neueste zuerst</option>
            <option value="name">Name A-Z</option>
            <option value="size_bytes">Grösse</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FolderOpen size={48} className="mb-4 text-muted" />
          <h3 className="text-lg font-medium text-muted">Keine Dokumente</h3>
          <p className="mt-1 text-sm text-muted">
            {search ? 'Keine Dokumente gefunden.' : 'Laden Sie Ihr erstes Dokument hoch.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <div className="grid min-w-[650px] grid-cols-[minmax(160px,1fr)_120px_100px_100px_80px] gap-4 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted">
            <span>Name</span>
            <span>Kategorie</span>
            <span>Grösse</span>
            <span>Datum</span>
            <span></span>
          </div>
          {filtered.map(doc => (
            <ContextMenu
              key={doc.id}
              actions={[
                { label: 'Anzeigen', onClick: () => handleDownload(doc) },
                { label: 'Entfernen', onClick: () => handleDeactivate(doc.id), destructive: true },
              ]}
            >
              <div
                className="grid min-w-[650px] grid-cols-[minmax(160px,1fr)_120px_100px_100px_80px] items-center gap-4 border-b border-border px-4 py-3 last:border-0 hover:bg-card-hover"
              >
              <div className="flex items-center gap-3 min-w-0">
                {getFileIcon(doc.mime_type)}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{doc.name}</p>
                  <p className="text-xs text-muted">{doc.mime_type || 'Unbekannt'}</p>
                </div>
              </div>
              <span className="rounded-full bg-card-hover px-2.5 py-1 text-xs font-medium text-muted text-center">
                {ENTITY_TYPES.find(t => t.value === doc.entity_type)?.label || doc.entity_type}
              </span>
              <span className="text-sm text-muted">{formatFileSize(doc.size_bytes)}</span>
              <span className="text-xs text-muted">{formatDate(doc.created_at)}</span>
              <div className="relative flex items-center justify-end gap-1">
                <button
                  onClick={() => handleDownload(doc)}
                  className="rounded p-1.5 text-muted hover:bg-card-hover hover:text-accent"
                  title="Herunterladen"
                >
                  <Download size={14} />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button aria-label={`Aktionen für ${doc.name}`} className="rounded p-1.5 text-muted hover:bg-card-hover">
                      <MoreVertical size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={() => void handleDownload(doc)} className="gap-2">
                      <Eye size={14} /> Anzeigen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void handleDeactivate(doc.id)} className="gap-2 text-danger focus:text-danger">
                      <Trash2 size={14} /> Entfernen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              </div>
            </ContextMenu>
          ))}
        </div>
      )}

      <Dialog open={showUpload} onOpenChange={(open) => { if (!uploading) setShowUpload(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Dokument hochladen</DialogTitle>
              <DialogDescription>Dateien einer Kategorie und Referenz zuordnen.</DialogDescription>
            </DialogHeader>
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}

            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">Kategorie</label>
                <select
                  value={uploadEntity.type}
                  onChange={(e) => setUploadEntity(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                >
                  <option value="plan">Marketingplan</option>
                  <option value="activity">Aktivität</option>
                  <option value="campaign">Kampagne</option>
                  <option value="supplier">Zulieferer</option>
                  <option value="profile">Profil</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">Referenz-ID (Pflicht)</label>
                <input
                  type="text"
                  value={uploadEntity.id}
                  onChange={(e) => setUploadEntity(prev => ({ ...prev, id: e.target.value }))}
                  placeholder="z. B. Plan-/Aktivitäts-/Kampagnen-UUID"
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm dark:text-foreground"
                />
              </div>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              role="button"
              tabIndex={uploading ? -1 : 0}
              aria-disabled={uploading}
              onKeyDown={(event) => { if (!uploading && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); fileInputRef.current?.click(); } }}
              onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors',
                dragActive
                  ? 'border-border bg-card-hover dark:bg-primary-hover/20'
                  : 'border-border hover:border-border'
              )}
            >
              {uploading ? (
                <Loader2 size={32} className="mb-2 animate-spin text-foreground" />
              ) : (
                <Upload size={32} className="mb-2 text-muted" />
              )}
              <p className="text-sm font-medium text-muted">
                {uploading ? 'Wird hochgeladen...' : 'Dateien hier ablegen oder klicken'}
              </p>
              <p className="mt-1 text-xs text-muted">PDF, Bilder, Excel, Word bis 50 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                disabled={uploading}
                multiple
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
              />
            </div>
          </DialogContent>
      </Dialog>
    </div>
  );
}
